import React from "react";
import { PieChart as RechartsPie, Pie, Cell, Legend, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, RadialBarChart, RadialBar, AreaChart, Area, LineChart, Line, FunnelChart, Funnel, LabelList } from "recharts";
import M2CDashboard from "./M2CDashboard";

/* SAP Fiori Horizon Qualitative Chart Palette */
const SAP_COLORS = [
  "#0070F2", "#C87B00", "#1B6B3D", "#AA0808", "#0057D2",
  "#046C7A", "#633795", "#BB7100", "#256F3A", "#D20A0A",
  "#7858FF", "#049F9A",
];
const SAP_SEMANTIC = { success: "#1B6B3D", warning: "#C87B00", error: "#AA0808", info: "#0070F2", neutral: "#5B738B" };

/**
 * Fiori Overview Page (KPI tiles + Analytical cards).
 * - Replaces gradient cards with calm, flat Numeric Tiles
 * - Status colors follow Fiori semantic palette
 */
function Dashboard({ filteredData, source }) {
  const totalExceptions = filteredData.length;

  // Status counts (with the same legacy mapping rules as before)
  const statusCounts = filteredData.reduce((acc, item) => {
    const raw = source === "payment" ? item.exception_status : item.status;
    let s = (raw || "UNKNOWN").toUpperCase().trim();
    if (s === "SENT") s = "APPROVED";
    else if (s === "CORRECTION IN PROGRESS") s = "DECLINED";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const totalAmount = filteredData.reduce(
    (sum, item) =>
      sum +
      (parseFloat(
        source === "m2c" || source === "payment"
          ? item.expected_amount
          : item.amount,
      ) || 0),
    0,
  );

  const exceptionsByType = filteredData.reduce((acc, item) => {
    const type =
      source === "m2c"
        ? item.case_type || "Unknown"
        : source === "p2p"
        ? item.exception_name || "Unknown"
        : source === "credit-memo"
        ? item.exception_category || "Unknown"
        : item.exception_type || "Unknown";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  const scenarioDetected =
    source === "m2c"
      ? filteredData.reduce((acc, item) => {
          const scenario = item.scenario_detected || "Unknown";
          acc[scenario] = (acc[scenario] || 0) + 1;
          return acc;
        }, {})
      : {};

  // Executive metrics for O2C
  const executiveMetrics =
    source === "o2c"
      ? {
          invoiceVolume: totalExceptions,
          aging: filteredData.reduce(
            (acc, item) => {
              if (item.invoice_date) {
                const d = new Date(item.invoice_date);
                const days = Math.floor(
                  (Date.now() - d) / (1000 * 60 * 60 * 24),
                );
                if (days <= 30) acc.current += 1;
                else if (days <= 60) acc.thirtyDays += 1;
                else if (days <= 90) acc.sixtyDays += 1;
                else acc.overNinety += 1;
              }
              return acc;
            },
            { current: 0, thirtyDays: 0, sixtyDays: 0, overNinety: 0 },
          ),
          errorRate:
            totalExceptions > 0
              ? (
                  (((statusCounts["DECLINED"] || 0) +
                    (statusCounts["INVOICE CANCELLED"] || 0) +
                    (statusCounts["OVERDUE"] || 0) +
                    (statusCounts["DISPUTE"] || 0)) /
                    totalExceptions) *
                  100
                ).toFixed(1)
              : 0,
          disputeCount:
            (statusCounts.DECLINED || 0) + (statusCounts.DISPUTED || 0),
          arBacklog: (() => {
            const included = ["OVERDUE", "DECLINED", "CORRECTION IN PROGRESS"];
            return filteredData
              .filter((i) => included.includes((i.status || "").toUpperCase()))
              .reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
          })(),
        }
      : null;

  // Map a raw status name to a Fiori Horizon semantic indicator color
  const statusSemantic = (status) => {
    const s = status.toUpperCase();
    if (
      ["APPROVED", "RESOLVED", "COMPLETED", "PAID", "MATCHED"].includes(s)
    )
      return { dot: "#1B6B3D", text: "text-fiori-success" };
    if (
      ["DECLINED", "OVERDUE", "REJECTED", "ERROR", "INVOICE CANCELLED"].includes(
        s,
      )
    )
      return { dot: "#AA0808", text: "text-fiori-error" };
    if (
      [
        "PENDING",
        "IN PROGRESS",
        "PROCESSING",
        "DISPUTE",
        "DISPUTED",
        "OPEN",
      ].includes(s)
    )
      return { dot: "#C87B00", text: "text-fiori-warning" };
    return { dot: "#0070F2", text: "text-fiori-info" };
  };

  return (
    <div className="space-y-4">
      {/* Executive KPI strip (O2C only - legacy, replaced by O2CDashboard) */}
      {false && source === "o2c" && executiveMetrics && (
        <section className="fiori-card">
          <div className="fiori-card-header flex items-center justify-between">
            <div>
              <h2 className="fiori-card-title">Executive Overview</h2>
              <p className="fiori-card-subtitle">
                Key receivables performance indicators
              </p>
            </div>
          </div>
          <div className="fiori-card-body grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile
              label="Invoice Volume"
              value={executiveMetrics.invoiceVolume}
            />
            <KpiTile
              label="Error Rate"
              value={`${executiveMetrics.errorRate}%`}
              tone={
                Number(executiveMetrics.errorRate) > 20 ? "error" : "warning"
              }
            />
            <KpiTile
              label="AR Backlog"
              value={`USD ${executiveMetrics.arBacklog.toLocaleString("en-US", {
                maximumFractionDigits: 0,
              })}`}
              tone="info"
            />
            <KpiTile
              label="Aging 90+ days"
              value={executiveMetrics.aging.overNinety}
              footer={`Total: ${Object.values(executiveMetrics.aging).reduce(
                (a, b) => a + b,
                0,
              )}`}
              tone={
                executiveMetrics.aging.overNinety > 0 ? "error" : "neutral"
              }
            />
          </div>
        </section>
      )}

      {/* Summary tiles */}
      {source !== "m2c" && source !== "p2p" && source !== "o2c" && (
      <section className="flex flex-wrap gap-3">
        <KpiTile
          label="Total Exceptions"
          value={totalExceptions}
          tone="info"
        />
        {Object.entries(statusCounts).map(([status, count]) => {
          const tone = statusSemantic(status);
          // Map the dot back to a tone enum for KpiTile
          let kind = "neutral";
          if (tone.dot === "#188918") kind = "success";
          else if (tone.dot === "#BB0000") kind = "error";
          else if (tone.dot === "#E76500") kind = "warning";
          else if (tone.dot === "#0A6ED1") kind = "info";
          const displayStatus = status.replace(/_/g, " ");
          return (
            <KpiTile
              key={status}
              label={displayStatus}
              value={count}
              tone={kind}
            />
          );
        })}
      </section>
      )}

      {/* Total amount (only when amounts are available) */}
      {source !== "m2c" && source !== "p2p" && source !== "o2c" && (
        <section className="fiori-card">
          <div className="fiori-card-body flex items-center justify-between">
            <div>
              <p className="fiori-tile-label">Total Amount</p>
              <p className="text-3xl font-light text-fiori-text leading-tight mt-1">
                <span className="text-base font-medium text-fiori-text-secondary mr-2">
                  USD
                </span>
                {totalAmount.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
            <span className="fiori-status-info">Sum of open exceptions</span>
          </div>
        </section>
      )}

      {/* Analytical row */}
      {source === "m2c" && <M2CDashboard filteredData={filteredData} statusCounts={statusCounts} totalExceptions={totalExceptions} exceptionsByType={exceptionsByType} scenarioDetected={scenarioDetected} statusSemantic={statusSemantic} />}

      {source === "p2p" && <P2PDashboard filteredData={filteredData} statusCounts={statusCounts} totalExceptions={totalExceptions} exceptionsByType={exceptionsByType} />}

      {source === "o2c" && <O2CDashboard filteredData={filteredData} statusCounts={statusCounts} totalExceptions={totalExceptions} exceptionsByType={exceptionsByType} />}

      {source !== "m2c" && source !== "p2p" && source !== "o2c" && (
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Exceptions by type - bar for non-m2c */}
        {source !== "credit-memo" ? (
        <div className="fiori-card">
          <div className="fiori-card-header">
            <h3 className="fiori-card-title">Exceptions by Type</h3>
            <p className="fiori-card-subtitle">Top 5 categories</p>
          </div>
          <div className="fiori-card-body space-y-3">
            {Object.entries(exceptionsByType)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([type, count]) => (
                <BarRow
                  key={type}
                  label={type}
                  value={count}
                  total={totalExceptions}
                />
              ))}
            {Object.keys(exceptionsByType).length === 0 && (
              <p className="text-xs text-fiori-text-secondary">
                No data available.
              </p>
            )}
          </div>
        </div>
        ) : null}

        {/* Aging chart for O2C */}
        {source === "o2c" && executiveMetrics && (
          <div className="fiori-card">
            <div className="fiori-card-header">
              <h3 className="fiori-card-title">Invoice Aging</h3>
              <p className="fiori-card-subtitle">By bucket</p>
            </div>
            <div className="fiori-card-body space-y-3">
              {[
                {
                  label: "Current (0-30 days)",
                  count: executiveMetrics.aging.current,
                  color: "var(--fiori-success)",
                },
                {
                  label: "31-60 days",
                  count: executiveMetrics.aging.thirtyDays,
                  color: "#E9A800",
                },
                {
                  label: "61-90 days",
                  count: executiveMetrics.aging.sixtyDays,
                  color: "var(--fiori-warning)",
                },
                {
                  label: "90+ days",
                  count: executiveMetrics.aging.overNinety,
                  color: "var(--fiori-error)",
                },
              ].map(({ label, count, color }) => {
                const total = Object.values(executiveMetrics.aging).reduce(
                  (a, b) => a + b,
                  0,
                );
                return (
                  <BarRow
                    key={label}
                    label={label}
                    value={count}
                    total={total}
                    color={color}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Credit Memo: Top Customers */}
        {source === "credit-memo" && (() => {
          const customerCounts = filteredData.reduce((acc, item) => {
            const vendor = item.vendor || "Unknown";
            acc[vendor] = (acc[vendor] || 0) + 1;
            return acc;
          }, {});
          return (
            <div className="fiori-card">
              <div className="fiori-card-header">
                <h3 className="fiori-card-title">Top Customers</h3>
                <p className="fiori-card-subtitle">By order count</p>
              </div>
              <div className="fiori-card-body space-y-3">
                {Object.entries(customerCounts)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5)
                  .map(([customer, count]) => (
                    <BarRow
                      key={customer}
                      label={customer}
                      value={count}
                      total={totalExceptions}
                      color="var(--fiori-brand)"
                    />
                  ))}
                {Object.keys(customerCounts).length === 0 && (
                  <p className="text-xs text-fiori-text-secondary">No data available.</p>
                )}
              </div>
            </div>
          );
        })()}

        {/* Credit Memo: Amount by Status */}
        {source === "credit-memo" && (() => {
          const amountByStatus = filteredData.reduce((acc, item) => {
            const s = (item.status || "Unknown").toUpperCase();
            acc[s] = (acc[s] || 0) + (parseFloat(item.amount) || 0);
            return acc;
          }, {});
          const maxAmount = Math.max(...Object.values(amountByStatus), 1);
          return (
            <div className="fiori-card">
              <div className="fiori-card-header">
                <h3 className="fiori-card-title">Amount by Status</h3>
                <p className="fiori-card-subtitle">USD totals per status</p>
              </div>
              <div className="fiori-card-body space-y-3">
                {Object.entries(amountByStatus)
                  .sort((a, b) => b[1] - a[1])
                  .map(([st, amt]) => (
                    <div key={st}>
                      <div className="flex justify-between mb-1">
                        <span className="text-xs font-medium text-fiori-text">{st}</span>
                        <span className="text-xs font-semibold text-fiori-text-secondary">
                          ${amt.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                      <div className="w-full bg-fiori-page-2 rounded h-2">
                        <div
                          className="h-2 rounded transition-all duration-300"
                          style={{ width: `${(amt / maxAmount) * 100}%`, backgroundColor: "var(--fiori-info)" }}
                        />
                      </div>
                    </div>
                  ))}
                {Object.keys(amountByStatus).length === 0 && (
                  <p className="text-xs text-fiori-text-secondary">No data available.</p>
                )}
              </div>
            </div>
          );
        })()}

        {/* Payment Reconciliation: Top Providers */}
        {source === "payment" && (() => {
          const providerCounts = filteredData.reduce((acc, item) => {
            const provider = item.provider_name || "Unknown";
            acc[provider] = (acc[provider] || 0) + 1;
            return acc;
          }, {});
          return (
            <div className="fiori-card">
              <div className="fiori-card-header">
                <h3 className="fiori-card-title">Top Providers</h3>
                <p className="fiori-card-subtitle">By exception count</p>
              </div>
              <div className="fiori-card-body space-y-3">
                {Object.entries(providerCounts)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5)
                  .map(([provider, count]) => (
                    <BarRow
                      key={provider}
                      label={provider}
                      value={count}
                      total={totalExceptions}
                      color="var(--fiori-brand)"
                    />
                  ))}
                {Object.keys(providerCounts).length === 0 && (
                  <p className="text-xs text-fiori-text-secondary">No data available.</p>
                )}
              </div>
            </div>
          );
        })()}

        {/* Payment Reconciliation: Variance Analysis */}
        {source === "payment" && (() => {
          const withVariance = filteredData.filter(
            (i) => i.expected_amount && i.received_amount,
          );
          const totalExpected = withVariance.reduce(
            (sum, i) => sum + (parseFloat(i.expected_amount) || 0), 0,
          );
          const totalReceived = withVariance.reduce(
            (sum, i) => sum + (parseFloat(i.received_amount) || 0), 0,
          );
          const variance = totalExpected - totalReceived;
          const maxVal = Math.max(totalExpected, totalReceived, 1);
          return (
            <div className="fiori-card">
              <div className="fiori-card-header">
                <h3 className="fiori-card-title">Payment Variance</h3>
                <p className="fiori-card-subtitle">Expected vs Received</p>
              </div>
              <div className="fiori-card-body space-y-3">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs font-medium text-fiori-text">Expected</span>
                    <span className="text-xs font-semibold text-fiori-text-secondary">
                      ${totalExpected.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  <div className="w-full bg-fiori-page-2 rounded h-2">
                    <div className="h-2 rounded" style={{ width: `${(totalExpected / maxVal) * 100}%`, backgroundColor: "var(--fiori-brand)" }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs font-medium text-fiori-text">Received</span>
                    <span className="text-xs font-semibold text-fiori-text-secondary">
                      ${totalReceived.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  <div className="w-full bg-fiori-page-2 rounded h-2">
                    <div className="h-2 rounded" style={{ width: `${(totalReceived / maxVal) * 100}%`, backgroundColor: "var(--fiori-success)" }} />
                  </div>
                </div>
                <div className="pt-2 border-t border-fiori-border-light flex justify-between items-center">
                  <span className="text-xs font-semibold text-fiori-text">Net Variance</span>
                  <span className={`text-sm font-bold ${variance > 0 ? "text-fiori-error" : variance < 0 ? "text-fiori-success" : "text-fiori-text"}`}>
                    {variance > 0 ? "-" : "+"}${Math.abs(variance).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}
      </section>
      )}

      {/* Status distribution - donut for non-m2c sources */}
      {source !== "m2c" && source !== "p2p" && source !== "o2c" && (
      <section className="fiori-card">
        <div className="fiori-card-header">
          <h3 className="fiori-card-title">Status Distribution</h3>
          <p className="fiori-card-subtitle">Share of total exceptions</p>
        </div>
        <div className="fiori-card-body flex flex-wrap gap-6 justify-around">
          {Object.entries(statusCounts).map(([status, count]) => {
            const tone = statusSemantic(status);
            const percentage =
              totalExceptions > 0
                ? Math.round((count / totalExceptions) * 100)
                : 0;
            return (
              <div key={status} className="text-center">
                <Donut
                  percentage={percentage}
                  color={tone.dot}
                />
                <p
                  className={`mt-2 text-xs font-semibold uppercase tracking-wide ${tone.text}`}
                >
                  {status}
                </p>
                <p className="text-xs text-fiori-text-secondary">
                  {count} item{count === 1 ? "" : "s"}
                </p>
              </div>
            );
          })}
          {Object.keys(statusCounts).length === 0 && (
            <p className="text-xs text-fiori-text-secondary">
              No data to display.
            </p>
          )}
        </div>
      </section>
      )}
    </div>
  );
}

/* ---------- Sub-components ---------- */

function KpiTile({ label, value, tone = "neutral", footer }) {
  const toneToBar = {
    success: "bg-fiori-success",
    error: "bg-fiori-error",
    warning: "bg-fiori-warning",
    info: "bg-fiori-info",
    neutral: "bg-fiori-neutral",
  };
  const toneToValue = {
    success: "text-fiori-success",
    error: "text-fiori-error",
    warning: "text-fiori-warning",
    info: "text-fiori-text",
    neutral: "text-fiori-text",
  };
  return (
    <div className="fiori-tile relative overflow-hidden flex-1 min-w-[160px]">
      <span
        className={`absolute left-0 top-0 bottom-0 w-1 ${toneToBar[tone]}`}
      />
      <p className="fiori-tile-label pl-2">{label}</p>
      <p className={`fiori-tile-value pl-2 ${toneToValue[tone]}`}>{value}</p>
      {footer && (
        <p className="text-xs text-fiori-text-secondary pl-2 mt-1">{footer}</p>
      )}
    </div>
  );
}

function BarRow({ label, value, total, color = "var(--fiori-brand)" }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-xs font-medium text-fiori-text">{label}</span>
        <span className="text-xs font-semibold text-fiori-text-secondary">
          {value}
        </span>
      </div>
      <div className="w-full bg-fiori-page-2 rounded h-2">
        <div
          className="h-2 rounded transition-all duration-300"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function Donut({ percentage, color }) {
  const r = 40;
  const c = 2 * Math.PI * r;
  const filled = (percentage / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center w-24 h-24">
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} stroke="#EAECEE" strokeWidth="10" fill="none" />
        <circle
          cx="50"
          cy="50"
          r={r}
          stroke={color}
          strokeWidth="10"
          fill="none"
          strokeDasharray={`${filled} ${c}`}
          strokeLinecap="round"
        />
      </svg>
      <span
        className="absolute text-base font-semibold"
        style={{ color }}
      >
        {percentage}%
      </span>
    </div>
  );
}

function PieChart({ data, colorMap, showTotal }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((sum, [, v]) => sum + v, 0);
  if (total === 0) return <p className="text-sm text-fiori-text-secondary">No data available.</p>;

  const chartData = entries.map(([label, value], idx) => {
    const displayLabel = label
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return {
      name: displayLabel,
      value,
      color: colorMap?.[label] || SAP_COLORS[idx % SAP_COLORS.length],
    };
  });

  const isDonut = showTotal !== undefined;

  return (
    <div className="w-full h-[280px] relative">
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPie>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            outerRadius={90}
            innerRadius={isDonut ? 45 : 0}
            dataKey="value"
            nameKey="name"
            labelLine={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name) => [`${value} (${((value / total) * 100).toFixed(1)}%)`, name]}
          />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            iconType="square"
            iconSize={10}
            wrapperStyle={{ fontSize: "11px" }}
            formatter={(value, entry) => `${value} ${entry.payload.value} (${((entry.payload.value / total) * 100).toFixed(1)}%)`}
          />
        </RechartsPie>
      </ResponsiveContainer>
      {isDonut && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ left: "-20%" }}>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-800">{showTotal}</div>
            <div className="text-[10px] text-gray-500">Total</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── O2C Dashboard (same visual style as M2C/P2P) ─── */
function O2CDashboard({ filteredData, statusCounts, totalExceptions, exceptionsByType }) {
  // Customer distribution
  const customerCounts = filteredData.reduce((acc, item) => {
    const customer = item.customer_name || "Unknown";
    acc[customer] = (acc[customer] || 0) + 1;
    return acc;
  }, {});

  // Amount by status
  const amountByStatus = filteredData.reduce((acc, item) => {
    const s = (item.status || "UNKNOWN").toUpperCase().replace(/_/g, " ");
    acc[s] = (acc[s] || 0) + (parseFloat(item.amount) || 0);
    return acc;
  }, {});

  // Total amount
  const totalAmount = filteredData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  // Invoice aging
  const aging = filteredData.reduce(
    (acc, item) => {
      if (item.invoice_date) {
        const d = new Date(item.invoice_date);
        const days = Math.floor((Date.now() - d) / (1000 * 60 * 60 * 24));
        if (days <= 30) acc.current += 1;
        else if (days <= 60) acc.thirtyDays += 1;
        else if (days <= 90) acc.sixtyDays += 1;
        else acc.overNinety += 1;
      }
      return acc;
    },
    { current: 0, thirtyDays: 0, sixtyDays: 0, overNinety: 0 },
  );
  const agingTotal = Object.values(aging).reduce((a, b) => a + b, 0);

  // Resolution funnel
  const openCount = statusCounts["OPEN"] || 0;
  const inProgressCount = statusCounts["IN_PROGRESS"] || statusCounts["IN PROGRESS"] || 0;
  const resolvedCount = statusCounts["RESOLVED"] || statusCounts["APPROVED"] || 0;
  const declinedCount = statusCounts["DECLINED"] || 0;
  const funnelData = [
    { name: "Total Invoices", value: totalExceptions },
    { name: "Open", value: openCount },
    { name: "In Progress", value: inProgressCount },
    { name: "Resolved", value: resolvedCount },
    { name: "Declined", value: declinedCount },
  ].filter(item => item.value > 0);

  // Trend by invoice date
  const trendData = (() => {
    const byDate = filteredData.reduce((acc, item) => {
      const date = item.invoice_date || item.updated_at;
      if (date) {
        const d = new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
        acc[d] = (acc[d] || 0) + 1;
      }
      return acc;
    }, {});
    const entries = Object.entries(byDate).slice(-12);
    if (entries.length > 1) {
      return entries.map(([date, count]) => ({ date, cases: count }));
    }
    return [];
  })();

  return (
    <>
      {/* Row 1: Status Donut, Exception Types Bar, Top Customers Bar */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Status Distribution — Donut */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Status Distribution</h3>
          <p className="text-xs text-gray-400 mb-4">Invoice exception status</p>
          <P2PDonutCard data={statusCounts} />
        </div>

        {/* Exception Types — Bar */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Exceptions by Type</h3>
          <p className="text-xs text-gray-400 mb-4">Top categories</p>
          <P2PBarChart data={exceptionsByType} />
        </div>

        {/* Top Customers — Bar */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Top Customers</h3>
          <p className="text-xs text-gray-400 mb-4">By exception count</p>
          <P2PBarChart data={customerCounts} />
        </div>
      </section>

      {/* Row 2: Invoice Aging, Resolution Funnel */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
       

        {/* Resolution Funnel */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Resolution Funnel</h3>
          <p className="text-xs text-gray-400 mb-4">Invoice pipeline</p>
          <div className="space-y-3 pt-2">
            {funnelData.map((item, i) => {
              const maxVal = funnelData[0].value || 1;
              const pct = Math.max((item.value / maxVal) * 100, 10);
              const opacity = 1 - i * 0.15;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div
                      className="h-8 rounded-lg flex items-center px-3 transition-all"
                      style={{ width: `${pct}%`, background: "#2563eb", opacity, minWidth: "40px" }}
                    >
                      <span className="text-white text-xs font-bold">{item.value}</span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 font-medium min-w-[110px]">{item.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Row 3: Total Amount + Trend */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Total Amount by Status */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
          <div className="flex items-start justify-between mb-1">
            <div>
              <h3 className="text-base font-semibold text-gray-800">Total Exception Amount</h3>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                ${totalAmount.toLocaleString("en-US", { maximumFractionDigits: 0 })}
              </p>
              <p className="text-sm text-blue-600 font-medium mt-0.5">{totalExceptions} invoices</p>
            </div>
            <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full border border-gray-100">USD</span>
          </div>
          <div className="space-y-3 mt-6">
            {Object.entries(amountByStatus)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([status, amount]) => {
                const maxAmt = Math.max(...Object.values(amountByStatus), 1);
                return (
                  <div key={status}>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs font-medium text-gray-700">{status}</span>
                      <span className="text-xs font-semibold text-gray-500">${amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded h-2">
                      <div className="h-2 rounded transition-all duration-300" style={{ width: `${(amount / maxAmt) * 100}%`, background: "#2563eb" }} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Invoice Trend */}
        {trendData.length > 1 ? (
          <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
            <h3 className="text-base font-semibold text-gray-800 mb-1">Invoice Trend</h3>
            <p className="text-xs text-gray-400 mb-4">Exceptions over time</p>
            <div className="w-full h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#1f2937" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#1f2937" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "12px" }} />
                  <Area type="monotone" dataKey="cases" stroke="#2563eb" strokeWidth={2.5} fill="rgba(37, 99, 235, 0.08)" dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff", fill: "#2563eb" }} name="Invoices" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6 flex items-center justify-center">
            <p className="text-sm text-gray-400">Trend data not available</p>
          </div>
        )}
      </section>
    </>
  );
}

/* ─── P2P Dashboard (same visual style as M2C) ─── */
function P2PDashboard({ filteredData, statusCounts, totalExceptions, exceptionsByType }) {
  const P2P_DONUT_COLORS = ["#2563eb", "#3b82f6", "#60a5fa", "#93c5fd", "#1d4ed8", "#1e40af"];

  // Vendor distribution
  const vendorCounts = filteredData.reduce((acc, item) => {
    const vendor = item.customer_name || "Unknown";
    acc[vendor] = (acc[vendor] || 0) + 1;
    return acc;
  }, {});

  // Amount by status
  const amountByStatus = filteredData.reduce((acc, item) => {
    const s = (item.status || "UNKNOWN").toUpperCase().replace(/_/g, " ");
    acc[s] = (acc[s] || 0) + (parseFloat(item.amount) || 0);
    return acc;
  }, {});

  // Total amount
  const totalAmount = filteredData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  // Resolution funnel
  const openCount = statusCounts["OPEN"] || 0;
  const inProgressCount = statusCounts["IN_PROGRESS"] || statusCounts["IN PROGRESS"] || 0;
  const resolvedCount = statusCounts["RESOLVED"] || 0;
  const funnelData = [
    { name: "Total Exceptions", value: totalExceptions },
    { name: "Open", value: openCount },
    { name: "In Progress", value: inProgressCount },
    { name: "Resolved", value: resolvedCount },
  ];

  // Trend - group by date if available, otherwise simulate
  const trendData = (() => {
    const byDate = filteredData.reduce((acc, item) => {
      const date = item.created_at || item.updated_at || item.timestamp;
      if (date) {
        const d = new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
        acc[d] = (acc[d] || 0) + 1;
      }
      return acc;
    }, {});
    const entries = Object.entries(byDate).slice(-10);
    if (entries.length > 1) {
      return entries.map(([date, count]) => ({ date, cases: count }));
    }
    // Fallback: just show total
    return [{ date: "Current", cases: totalExceptions }];
  })();

  return (
    <>
      {/* Row 1: Status Donut, Exception Types Bar, Top Vendors Bar */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Status Distribution — Donut */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Status Distribution</h3>
          <p className="text-xs text-gray-400 mb-4">Exception status breakdown</p>
          <P2PDonutCard data={statusCounts} />
        </div>

        {/* Exception Types — Bar */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Exceptions by Type</h3>
          <p className="text-xs text-gray-400 mb-4">Top categories</p>
          <P2PBarChart data={exceptionsByType} />
        </div>

        {/* Top Vendors — Bar */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Top Vendors</h3>
          <p className="text-xs text-gray-400 mb-4">By exception count</p>
          <P2PBarChart data={vendorCounts} />
        </div>
      </section>

      {/* Row 2: Resolution Funnel + Amount by Status + Trend */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Resolution Funnel */}
        {/* <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-1">Resolution Funnel</h3>
          <p className="text-xs text-gray-400 mb-4">Exception pipeline</p>
          <div className="space-y-3 pt-2">
            {funnelData.map((item, i) => {
              const maxVal = funnelData[0].value || 1;
              const pct = Math.max((item.value / maxVal) * 100, 10);
              const opacity = 1 - i * 0.2;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div
                      className="h-8 rounded-lg flex items-center px-3 transition-all"
                      style={{ width: `${pct}%`, background: "#2563eb", opacity, minWidth: "40px" }}
                    >
                      <span className="text-white text-xs font-bold">{item.value}</span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 font-medium min-w-[110px]">{item.name}</span>
                </div>
              );
            })}
          </div>
        </div> */}

        {/* Amount by Status + Total */}
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
          <div className="flex items-start justify-between mb-1">
            <div>
              <h3 className="text-base font-semibold text-gray-800">Total Exception Amount</h3>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                ${totalAmount.toLocaleString("en-US", { maximumFractionDigits: 0 })}
              </p>
              <p className="text-sm text-blue-600 font-medium mt-0.5">{totalExceptions} exceptions</p>
            </div>
            <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full border border-gray-100">USD</span>
          </div>
          <div className="space-y-3 mt-6">
            {Object.entries(amountByStatus)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([status, amount]) => {
                const maxAmt = Math.max(...Object.values(amountByStatus), 1);
                return (
                  <div key={status}>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs font-medium text-gray-700">{status}</span>
                      <span className="text-xs font-semibold text-gray-500">${amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded h-2">
                      <div className="h-2 rounded transition-all duration-300" style={{ width: `${(amount / maxAmt) * 100}%`, background: "#2563eb" }} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* Row 3: Trend if available */}
      {trendData.length > 1 && (
        <section className="grid grid-cols-1 gap-5">
          <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
            <h3 className="text-base font-semibold text-gray-800 mb-1">Exception Trend</h3>
            <p className="text-xs text-gray-400 mb-4">Cases over time</p>
            <div className="w-full h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ left: 0, right: 10, top: 10, bottom: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "12px" }} />
                  <Area type="monotone" dataKey="cases" stroke="#2563eb" strokeWidth={2.5} fill="rgba(37, 99, 235, 0.08)" dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff", fill: "#2563eb" }} name="Exceptions" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

/* ─── P2P Donut Card ─── */
function P2PDonutCard({ data }) {
  const P2P_COLORS = ["#2563eb", "#3b82f6", "#60a5fa", "#93c5fd", "#1d4ed8", "#1e40af"];
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const sum = entries.reduce((s, [, v]) => s + v, 0);
  if (sum === 0) return <p className="text-xs text-gray-400">No data</p>;
  const chartData = entries.map(([label, value], idx) => ({
    name: label.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    value,
    color: P2P_COLORS[idx % P2P_COLORS.length],
  }));

  const renderLabel = ({ cx, cy, midAngle, outerRadius, value }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const pct = ((value / sum) * 100).toFixed(0);
    return (
      <text x={x} y={y} fill="#6b7280" textAnchor={x > cx ? "start" : "end"} dominantBaseline="central" fontSize={10} fontWeight={500}>
        {value} ({pct}%)
      </text>
    );
  };

  return (
    <div className="w-full h-[240px]">
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPie>
          <Pie data={chartData} cx="50%" cy="45%" outerRadius={75} innerRadius={45} dataKey="value" nameKey="name" label={renderLabel} labelLine={{ stroke: "#d1d5db", strokeWidth: 1 }}>
            {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [`${v} (${((v / sum) * 100).toFixed(1)}%)`, n]} />
          <Legend layout="horizontal" align="center" verticalAlign="bottom" iconType="square" iconSize={10} wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} />
        </RechartsPie>
      </ResponsiveContainer>
    </div>
  );
}

/* ─── P2P Bar Chart ─── */
function P2PBarChart({ data }) {
  const barData = Object.entries(data)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, count]) => ({
      name: name.replace(/_/g, " "),
      count,
    }));
  if (barData.length === 0) return <p className="text-xs text-gray-400 text-center py-8">No data</p>;
  return (
    <div className="w-full h-[260px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={barData} margin={{ left: -10, right: 5, top: 5, bottom: 10 }}>
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#1f2937" }} axisLine={false} tickLine={false} angle={-30} textAnchor="end" height={70} interval={0} />
          <YAxis tick={{ fontSize: 10, fill: "#1f2937" }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "12px" }} formatter={(v, name, props) => [`${v} exception${v > 1 ? "s" : ""}`, props.payload.name]} />
          <Bar dataKey="count" fill="#2563eb" radius={[6, 6, 0, 0]} barSize={22} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default Dashboard;
